import { format } from 'date-fns/format';

/**
 * The number of distinct app colours in a day. With more apps than this, colours repeat in rank
 * order, so the thirteenth app has the colour of the first.
 */
const HUE_COUNT = 12;

export const hueForRank = (rank: number, count: number) =>
  (rank % HUE_COUNT) * (360 / Math.min(Math.max(count, 1), HUE_COUNT)) + 20;

export const appFill = (hue: number | null) =>
  hue === null ? 'var(--surface-active)' : `hsl(${hue} 60% 55% / 0.32)`;

export const appStroke = (hue: number | null) =>
  hue === null ? 'var(--border-strongest)' : `hsl(${hue} 55% 48%)`;

/**
 * Gives Chrome for `com.google.Chrome`, and notion for `notion.id`, whose last segment is too short
 * to be the name.
 */
export const appShortName = (app: string | null) => {
  if (app === null) return null;
  const segments = app.split('.').filter((segment) => segment.length > 0);
  const last = segments[segments.length - 1] ?? app;
  const before = segments[segments.length - 2];
  return last.length <= 2 && before !== undefined ? before : last;
};

/** Rules that the user or their team made. The screen calls what they hide redacted. */
const HIDING_RULES: Record<string, string> = {
  app: 'app rule',
  domain: 'domain rule',
  url_keyword: 'URL keyword rule',
  window_rule: 'window rule',
  browser_extension: 'browser extension',
  unidentified_app: 'unknown rule',
};

/** Reasons that Littlebird hides part of an identity without a rule from the user or their team. */
const NOT_CAPTURED_REASONS: Record<string, string> = {
  mandatory_exclusion: 'Littlebird',
  url_unavailable: 'url unavailable',
};

/** The name a card shows when a rule of the user or their team hid the app. */
export const HIDDEN_APP_NAME = 'redacted';

const hidingReason = (hiddenBy: string) =>
  NOT_CAPTURED_REASONS[hiddenBy] ??
  HIDING_RULES[hiddenBy] ??
  HIDING_RULES.unidentified_app;

/** The words after the app name on a card and its tooltip, such as `redacted · domain rule`. */
export const hiddenByWords = (hiddenBy: string, appName: string) => {
  const reason = hidingReason(hiddenBy);
  if (appName === HIDDEN_APP_NAME) return reason;
  return `${hiddenBy in NOT_CAPTURED_REASONS ? 'not captured' : 'redacted'} · ${reason}`;
};

export const hiddenByLabel = (hiddenBy: string | null) => {
  if (hiddenBy === null) return 'nobody';
  if (hiddenBy === 'mandatory_exclusion')
    return 'Littlebird (mandatory exclusion)';
  if (hiddenBy === 'url_unavailable') return 'Littlebird (url unavailable)';
  return `your rule (${hidingReason(hiddenBy)})`;
};

export const formatDuration = (ms: number) => {
  // Durations under a minute show seconds, because this screen exists to check short intervals
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  const totalMinutes = Math.round(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
};

/**
 * Clock times on this screen are 24-hour, whatever the app's time-format setting says, because the
 * screen is read beside observer logs, database rows, and its own time axis, which are all 24-hour.
 */
export const formatClock = (moment: Date | string) =>
  format(new Date(moment), 'HH:mm');

export const formatClockWithSeconds = (moment: Date | string) =>
  format(new Date(moment), 'HH:mm:ss');

export const formatClockRange = (start: Date | string, end: Date | string) =>
  `${formatClock(start)} – ${formatClock(end)}`;

export const formatClockRangeWithSeconds = (
  start: Date | string,
  end: Date | string
) => `${formatClockWithSeconds(start)} – ${formatClockWithSeconds(end)}`;
